import React, { useState } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Building2, Plus } from "lucide-react";
import { useFacility } from "@/context/FacilityContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

function AddFacilityDialog({ open, onOpenChange, onAdded }) {
  const [label, setLabel] = useState("");
  const [id, setId]       = useState("");
  const [cadence, setCadence] = useState("biweekly");
  const [saving, setSaving] = useState(false);

  const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").trim().replace(/\s+/g, "_").slice(0, 32);

  const handleLabel = (v) => {
    setLabel(v);
    // Auto-slug the id from the label if user hasn't typed a custom id yet.
    if (!id || id === slugify(label)) setId(slugify(v));
  };

  const submit = async () => {
    if (!label.trim() || !id.trim()) {
      toast.error("Enter a facility name.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/facilities", { id: id.trim(), label: label.trim(), cadence });
      toast.success(`Added ${data.facility.label} · ${data.facility.cadence === "monthly" ? "Monthly" : "Bi-weekly"}`);
      setLabel(""); setId(""); setCadence("biweekly");
      onOpenChange(false);
      onAdded?.(data.facility);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add facility");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add facility</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Facility name</Label>
            <Input
              value={label}
              onChange={(e) => handleLabel(e.target.value)}
              placeholder="e.g. Los Banos"
              data-testid="new-facility-label"
            />
          </div>
          <div>
            <Label>Short id <span className="text-xs text-slate-500 font-normal">(letters/digits/underscores)</span></Label>
            <Input
              value={id}
              onChange={(e) => setId(slugify(e.target.value))}
              placeholder="los_banos"
              data-testid="new-facility-id"
            />
          </div>
          <div>
            <Label>Cadence</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCadence("biweekly")}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  cadence === "biweekly" ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid="cadence-biweekly"
              >
                <div className="text-sm font-bold text-slate-900">Bi-weekly</div>
                <div className="text-[11px] text-slate-500 leading-snug">Two-week pay periods · same as Modesto, Davis, Coast, etc.</div>
              </button>
              <button
                type="button"
                onClick={() => setCadence("monthly")}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  cadence === "monthly" ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid="cadence-monthly"
              >
                <div className="text-sm font-bold text-slate-900">Monthly</div>
                <div className="text-[11px] text-slate-500 leading-snug">Calendar-month schedule · same as Auburn / Solano.</div>
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="new-facility-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !label.trim() || !id.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="new-facility-save">
            {saving ? "Adding…" : "Add facility"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FacilityContextSwitcher() {
  const { facilities, facilityId, setFacilityId, refresh } = useFacility();
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);

  if (!user || user === false) return null;
  if (!facilities.length) return null;
  // Employees with only one facility see a static badge; multi-facility employees
  // get the same switcher as managers so they can view every facility they have access to.
  if (user.role === "employee" && facilities.length <= 1) {
    const f = facilities.find((x) => x.id === facilityId);
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700" data-testid="facility-static">
        <Building2 size={14} className="text-blue-600" />
        <span className="font-medium">{f?.label || "—"}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="facility-switcher">
      <Building2 size={14} className="text-blue-600" />
      <Select value={facilityId || undefined} onValueChange={setFacilityId}>
        <SelectTrigger className="w-[110px] sm:w-[180px]" data-testid="facility-select">
          <SelectValue placeholder="Facility" />
        </SelectTrigger>
        <SelectContent>
          {facilities.map((f) => (
            <SelectItem key={f.id} value={f.id} data-testid={`facility-option-${f.id}`}>
              {f.label}
              {f.cadence === "monthly" && (
                <span className="ml-1 text-[9px] uppercase tracking-widest text-amber-700">· Mo</span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {user.role === "admin" && (
        <>
          <Button
            variant="outline"
            size="icon"
            className="border-blue-300 text-blue-700 hover:bg-blue-50 h-9 w-9 hidden sm:inline-flex"
            title="Add a new facility (admin only)"
            onClick={() => setAddOpen(true)}
            data-testid="add-facility-btn"
          >
            <Plus size={14} />
          </Button>
          <AddFacilityDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            onAdded={(fac) => { refresh?.(); setFacilityId?.(fac.id); }}
          />
        </>
      )}
    </div>
  );
}
